/**
 * @file aiPipeline.ts
 * @brief Super Admin → AI settings as a pipeline (W13, design locked DES
 *        23-10-2026, scratch/dispatcher/w13-ai-settings-mockup.html): the
 *        modules, their steps, the presets, the cost per hour and the
 *        per-language speech-to-text chain, all read from `system_settings`
 *        values. Pure, so it runs under `node --test`.
 *
 * @details
 * Nothing here writes a setting: `pick*` / `chainPatch` return a patch for the
 * page's draft, and the page's Save bar is the only thing that saves.
 *
 * Costs are the same estimates the 15-10 simple page used, in cents per
 * meeting-hour (13-10 / 14-10 cost runs): Deepgram 25.8c/h, Soniox first
 * about 13.2c/h, report gpt-4.1-mini 1.6 / gpt-4.1 7.2, fact check 0.5 / 2.5,
 * second look 0.5 / 2, Meeting Notes 3 / 8. Speech to text now follows the
 * first vendor of the chain for the language shown (Urdu: Deepgram first).
 * AssemblyAI has no estimate in that model yet, so it shows none rather than
 * a guess. W14: the Interpreter (per speaking hour) and Ask (per 100
 * questions) show LIST-PRICE estimates from the 23-10 unit-economics grid,
 * labelled so on the page.
 *
 * Backup orders mirror the backend: TTS PREFERENCE_ORDER deepgram → openai →
 * azure → google (BACKEND tts/index.mjs, keyless vendors skipped, a picked
 * vendor goes first); Interpreter ASR Auto = Deepgram, then OpenAI, and a
 * Soniox turn that fails uses Auto; the meeting / note chain is the saved
 * ASR_CHAIN_* order (BACKEND asr/diarizeBatch.mjs), Deepgram only when off.
 *
 * W16 (24-10-2026): the B16 Listen switches and the B18 Notes card (Switch /
 * SwitchCard); W15: RETIRED_MODELS are never offered.
 *
 * @references ./AiSettingsSimple.tsx, ./AiConfigurationCard.tsx (DEFAULTS), ./urduVoicePickers.ts
 * @author DES (prepared with Claude)
 * @date 23-10-2026
 */

import { allUrduVoiceSteps, urduVoiceSteps } from "./urduVoicePickers.ts";

export type Values = Record<string, string | undefined>;
type Option = { value: string; label: string };
type CatalogEntry = { label: string; configured: boolean; models: Option[]; urduVoices?: { male: Option[]; female: Option[] } };
export type CatalogLike = {
  TTS: Record<string, CatalogEntry | undefined>;
  ASR: Record<string, CatalogEntry | undefined>;
  LLM: Record<string, CatalogEntry | undefined>;
};

export type ModuleKey = "interpreter" | "meetings" | "notes" | "ask";
export type LangKey = "english" | "urdu" | "other";
export type IconKey =
  | "listen" | "translate" | "speak" | "names" | "write" | "check" | "second" | "plan" | "search"
  | "mic" | "voice" | "doc" | "question";

export type Ctx = {
  provider: string;
  catalog: CatalogLike;
  lang: LangKey;
  /** The Interpreter's translation models (schemas MT_MODEL_OPTIONS). */
  mtModels: Option[];
};

// ---------------------------------------------------------------- costs

/** Cents per meeting-hour by model (estimates, see the file header). */
export const REPORT_COST: Record<string, number> = { "gpt-4.1-mini": 1.6, "gpt-4.1": 7.2, "gpt-4o-mini": 0.6, "gpt-4o": 6 };
export const CHECK_COST: Record<string, number> = { "gpt-4.1-mini": 0.5, "gpt-4.1": 2.5, "gpt-4o-mini": 0.2, "gpt-4o": 2 };
export const LOOK_COST: Record<string, number> = { "gpt-4.1-mini": 0.5, "gpt-4.1": 2, "gpt-4o-mini": 0.2, "gpt-4o": 1.6 };
export const NOTES_COST: Record<string, number> = { "gpt-4.1-mini": 3, "gpt-4.1": 8, "gpt-4o-mini": 1, "gpt-4o": 7 };
/** Speech to text by the vendor that goes first; AssemblyAI not measured. */
export const ASR_COST: Record<string, number | undefined> = { deepgram: 25.8, soniox: 13.2, assemblyai: undefined };

// List-price estimates (W14, 23-10-2026, the unit-economics grid). Cents per
// SPEAKING hour for the Interpreter, per 100 questions for Ask.

/** A speaking hour is 710 chars of speech a minute (unit-economics grid). */
const SPEECH_CHARS_PER_HOUR = 710 * 60;
/** Voice list prices, cents per 1M chars (BACKEND ensure_pricing_catalog.js). */
const TTS_CENTS_PER_M_CHARS: Record<string, number> = { azure: 1500, deepgram: 1500, google: 3000, soniox: 1400 };
/** OpenAI gpt-4o-mini-tts bills audio, about $0.015 a spoken minute. */
const OPENAI_TTS_CENTS_PER_HOUR = 90;
export const TTS_HOUR_COST: Record<string, number> = {
  ...Object.fromEntries(Object.entries(TTS_CENTS_PER_M_CHARS).map(([p, c]) => [p, (c * SPEECH_CHARS_PER_HOUR) / 1e6])),
  openai: OPENAI_TTS_CENTS_PER_HOUR,
};
/** Live speech to text, cents per hour: Soniox real-time $0.12/h, Deepgram streaming, OpenAI gpt-4o-mini-transcribe. */
export const LIVE_ASR_HOUR_COST: Record<string, number> = { soniox: 12, deepgram: 25.8, openai: 18 };
/** OpenAI list prices, $ per 1M tokens [input, output]. */
const LLM_PRICE: Record<string, [number, number]> = {
  "gpt-4.1": [2, 8], "gpt-4.1-mini": [0.4, 1.6], "gpt-4o": [2.5, 10], "gpt-4o-mini": [0.15, 0.6],
};
/** B16: the Urdu loanword pass on a Soniox Urdu meeting, cents per meeting-hour (gpt-4.1-mini). */
export const LOANWORDS_COST = 2.5;

// ------------------------------------------------------- retired models

/**
 * Models OpenAI no longer serves (W15, 24-10-2026: gpt-4.1-nano answers 404).
 * They are never offered; a saved one shows RETIRED_NOTE until it is changed.
 */
export const RETIRED_MODELS: Record<string, string> = { "gpt-4.1-nano": "GPT-4.1 Nano" };
export const RETIRED_NOTE = "Not available, pick another model";
export const isRetired = (model: string | undefined) => model !== undefined && model in RETIRED_MODELS;

/** The catalog without retired models, for every picker (the backend still lists nano). */
export function dropRetiredModels<C extends CatalogLike>(catalog: C): C {
  const stage = (s: Record<string, CatalogEntry | undefined>) =>
    Object.fromEntries(Object.entries(s).map(([p, e]) => [p, e && { ...e, models: e.models.filter((m) => !isRetired(m.value)) }]));
  return { ...catalog, TTS: stage(catalog.TTS), ASR: stage(catalog.ASR), LLM: stage(catalog.LLM) };
}

/** A disabled choice for a saved retired model, so the step shows what is saved. */
const retiredChoice = (value: string, picked: string | undefined, badge: string): Choice[] =>
  picked !== undefined && isRetired(picked) ? [{ value, badge, name: RETIRED_MODELS[picked], disabled: RETIRED_NOTE }] : [];
/** Cents for one call of `inTok` / `outTok` tokens on each priced model. */
const perCall = (inTok: number, outTok: number, scale = 1) =>
  Object.fromEntries(Object.entries(LLM_PRICE).map(([m, [i, o]]) => [m, ((inTok * i + outTok * o) / 1e6) * 100 * scale]));
/** Translation: about 180 tokens a speech minute in and out. Soniox translates inside its listen price. */
export const MT_HOUR_COST = perCall(180 * 60, 180 * 60);
/**
 * Ask per 100 questions (ADR-050, prod 15-21 Oct): planner ~590 in + 64 out,
 * answer ~2,960 in + 250 out; list answers skip the answer call, so the
 * answer averages 0.74 of a call (0.151 c per Ask on gpt-4.1-mini).
 */
export const ASK_PLAN_COST = perCall(590, 64, 100);
export const ASK_ANSWER_COST = perCall(2960, 250, 100 * 0.739);

/** Share of meeting-hours on the long-meeting report. */
const LONG_SHARE = 0.25;
/** PAYG price (DES 02-10-2026): 5p a minute. */
export const USER_PENCE_PER_HOUR = 300;
export const CENTS_TO_PENCE = 0.79;

export const pence = (cents: number) => cents * CENTS_TO_PENCE;
export const fmtP = (cents: number) => {
  if (cents === 0) return "Included";
  const p = pence(cents);
  return `${p < 10 ? p.toFixed(1) : p.toFixed(0)}p`;
};

// ------------------------------------------------------------ languages

export const LANGS: { key: LangKey; label: string; chainKey: string }[] = [
  { key: "english", label: "English", chainKey: "ASR_CHAIN_ENGLISH" },
  { key: "urdu", label: "Urdu & mixed", chainKey: "ASR_CHAIN_URDU" },
  { key: "other", label: "Other languages", chainKey: "ASR_CHAIN_OTHER" },
];

const ASR_VENDORS: Record<string, { name: string; tag: string; note: string }> = {
  soniox: { name: "Soniox", tag: "async", note: "Cheapest, most words caught" },
  deepgram: { name: "Deepgram", tag: "Nova-3", note: "Best for Urdu-English mix" },
  assemblyai: { name: "AssemblyAI", tag: "EU", note: "Strong speaker labels in English" },
};
export const ASR_VENDOR_IDS = Object.keys(ASR_VENDORS);
const asrName = (v: string) => ASR_VENDORS[v]?.name ?? v;

/**
 * The meeting / note speech-to-text order for a language, read from the
 * saved chain row. Off = Deepgram only (the backend ignores the order then).
 */
export function chainFor(v: Values, lang: LangKey): { on: boolean; order: string[] } {
  const on = v.ASR_CHAIN_ENABLED === "true";
  if (!on) return { on, order: ["deepgram"] };
  const key = LANGS.find((l) => l.key === lang)!.chainKey;
  const seen = new Set<string>();
  const order = (v[key] ?? "")
    .split(",")
    .map((s) => s.trim().toLowerCase())
    .filter((s) => ASR_VENDORS[s] && !seen.has(s) && seen.add(s));
  return { on, order };
}

/** Patch that writes one language's chain order. */
export function chainPatch(lang: LangKey, order: string[]): Values {
  return { [LANGS.find((l) => l.key === lang)!.chainKey]: order.join(",") };
}

/** Put `vendor` first, the rest keep their order. */
export const withFirst = (order: string[], vendor: string) => [vendor, ...order.filter((x) => x !== vendor)];

/** Swap backup `i` (0 = first backup) with its neighbour; returns the full order. */
export function moveBackup(order: string[], i: number, dir: -1 | 1): string[] {
  const at = i + 1;
  const to = at + dir;
  if (to < 1 || to >= order.length) return order;
  const out = [...order];
  [out[at], out[to]] = [out[to], out[at]];
  return out;
}

// ------------------------------------------------------------- modules

export type ModuleDef = {
  key: ModuleKey;
  title: string;
  unit: string;
  noLang?: boolean;
  in: [IconKey, string];
  out: [IconKey, string];
  steps: string[];
  /** Short unit for the module tab. */
  tabUnit: string;
  /** Said next to the unit when the costs are list prices, not measured runs. */
  estimate?: string;
  /** Saved switches the module owns outside its steps (Meetings: Cost savers). */
  extraFields?: Field[];
  /** The card those switches are drawn in, under the timeline. */
  card?: SwitchCard;
  /** One saved text setting drawn under the timeline (Meetings: to-do starter categories). */
  textCard?: TextCard;
  /** Shown in the summary card, e.g. the routing rollback warning. */
  notice?: (v: Values) => string | null;
};

/**
 * Meetings "Cost savers" (B13, ADR-052, BACKEND costLevers.mjs): how OpenAI is
 * called, not which model. Each seeded OFF; the rows exist only after the B13
 * backend deploy, so the page offers them only when the server has the row.
 */
export type Switch = { key: string; name: string; line: string };
/**
 * A card with one text setting; `problem` is what the server would refuse (Save waits).
 * `toggle` is an on/off switch drawn above the text, with its own `missing`.
 */
export type TextCard = {
  key: string;
  title: string;
  intro: string;
  placeholder: string;
  missing: string;
  problem: (v: string | undefined) => string | null;
  toggle?: Switch & { missing: string };
};
/** A card of on/off switches; `missing` is said while the server has no row. */
export type SwitchCard = { title: string; intro: string; items: Switch[]; missing: string };

export const COST_SAVERS: Switch[] = [
  {
    key: "AI_PROMPT_CACHE_ENABLED",
    name: "Prompt cache",
    line: "Sends a cache key with each OpenAI call. Same prompts and answers; repeated parts cost a quarter. Saves about nothing today.",
  },
  {
    key: "AI_PROMPT_SLIM_ENABLED",
    name: "Slimmer fact check",
    line: "Sends a passage once when several items cite it (8-28% fewer fact-check tokens). Leave off: a quarter of verdicts changed in the test.",
  },
  {
    key: "AI_FLEX_TIER_ENABLED",
    name: "Flex tier",
    line: "Half price, slower, for steps nobody waits on, with the normal tier as backup. No effect on GPT-4.1 or GPT-4.1 Mini.",
  },
];
const onOff = (x: string | undefined) => (x === "true" ? "On" : "Off");

/**
 * Listen switches (B16, ADR-053, BACKEND diarizeBatch.mjs / voiceResegment.mjs),
 * each seeded OFF in 00177. The loanword pass runs only on the Urdu chain when
 * Soniox writes the transcript; the re-draw on any meeting Soniox writes.
 */
export const URDU_LOANWORDS: Switch = {
  key: "ASR_URDU_LOANWORDS_LATIN",
  name: "English words in English letters",
  line: `Urdu meetings on Soniox: English words back in English letters. A small GPT-4.1 Mini pass, about ${fmtP(LOANWORDS_COST)} per meeting-hour.`,
};
export const VOICE_RESEG: Switch = {
  key: "ASR_SONIOX_VOICE_RESEG",
  name: "Re-draw speakers",
  line: "Meetings on Soniox: re-draw speakers when Soniox mixes people (needs speaker embeddings on the server). No vendor cost.",
};
/** Notes: My notes × meeting report (B18, ADR-054, BACKEND noteMeetingReportFocus.mjs). */
export const NOTE_REPORT_FOCUSED: Switch = {
  key: "NOTE_REPORT_FOCUSED_INPUT",
  name: "Focused input",
  // B18 eval: 1.33 c → 0.87 c a report on gpt-4.1-mini.
  line: `My notes × meeting report: send only the relevant parts of the meeting. About a third cheaper a report (${fmtP(1.33)} → ${fmtP(0.87)}); 1 firm point lost in 34 in the test.`,
};
/**
 * Meetings: to-do grouping switch (F1a, ADR-062, BACKEND todoGrouping.service.mjs
 * groupingEnabled), seeded OFF. One gpt-4.1-mini call a meeting, about 0.046 c
 * (BACKEND da8e51c). Read per meeting, so a flip needs no deploy.
 */
export const TODO_GROUPING: Switch = {
  key: "TODO_GROUPING_ENABLED",
  name: "To-do grouping",
  // 0.046 c is 0.04p; written out because fmtP shows one decimal ("0.0p").
  line: "After each meeting, one small call gives every to-do a topic and a category. About 0.04p a meeting.",
};
export const TODO_GROUPING_FIELD: Field = { key: TODO_GROUPING.key, label: "To-do categories · To-do grouping", fmt: onOff };
/**
 * Meetings: to-do starter categories (F1a-2, ADR-062, BACKEND todoGrouping.mjs
 * parseStarterList). Comma separated; empty = the built-in 14. The backend
 * refuses a name over 4 words / 40 characters or more than 30 names, so the
 * page says so before Save. A change affects only new labelling.
 */
export const TODO_STARTERS_KEY = "TODO_STARTER_CATEGORIES";
export const TODO_STARTERS_BUILT_IN = [
  "Product", "Engineering", "Design", "Marketing", "Sales", "Customer Support", "Finance",
  "Legal & Compliance", "Operations", "People & HR", "Projects", "Research", "Admin", "Personal",
];
export function starterNames(raw: string | undefined): string[] {
  const seen = new Set<string>();
  return String(raw ?? "")
    .split(/[,\n]/)
    .map((p) => p.replace(/\s+/g, " ").trim())
    .filter((p) => p && !seen.has(p.toLowerCase()) && seen.add(p.toLowerCase()));
}
/** What the backend would refuse, or null. */
export function starterProblem(raw: string | undefined): string | null {
  const names = starterNames(raw);
  const long = names.find((n) => n.split(" ").length > 4 || n.length > 40);
  if (long) return `"${long}" is too long: at most 4 words and 40 characters a name.`;
  if (names.length > 30) return `${names.length} names: at most 30.`;
  return null;
}
export const TODO_STARTERS_FIELD: Field = {
  key: TODO_STARTERS_KEY,
  label: "To-do categories · Starter list",
  fmt: (x) => {
    const n = starterNames(x);
    return n.length ? `${n.length} names` : "Built-in 14";
  },
};

const switchFields = (items: Switch[], prefix: string): Field[] => items.map((c) => ({ key: c.key, label: `${prefix} · ${c.name}`, fmt: onOff }));

export const MODULES: ModuleDef[] = [
  { key: "interpreter", title: "Interpreter", unit: "per speaking hour", tabUnit: "per hour", estimate: "list-price estimate", in: ["mic", "Someone speaks"], out: ["voice", "The other person hears"], steps: ["i_listen", "i_translate", "i_speak"] },
  {
    key: "meetings",
    title: "Meetings",
    unit: "per meeting-hour",
    tabUnit: "per hour",
    in: ["mic", "Recording"],
    out: ["doc", "Minutes"],
    extraFields: [...switchFields(COST_SAVERS, "Cost savers"), TODO_GROUPING_FIELD, TODO_STARTERS_FIELD],
    textCard: {
      key: TODO_STARTERS_KEY,
      title: "To-do categories",
      intro: "Starter names every new to-do list may use from its first meeting, comma separated. Each is stored only when a to-do first gets it. Empty uses the built-in 14. A change affects new labelling only; stored to-dos keep their category.",
      placeholder: TODO_STARTERS_BUILT_IN.join(", "),
      missing: "Arrives with the F1a-2 backend deploy.",
      problem: starterProblem,
      toggle: { ...TODO_GROUPING, missing: "Arrives with the F1a backend deploy." },
    },
    card: {
      title: "Cost savers",
      intro: "How OpenAI is called, not which model. Same answers. Each is off until you turn it on.",
      items: COST_SAVERS,
      missing: "Arrives with the B13 backend deploy.",
    },
    steps: ["m_listen", "m_names", "m_write", "m_check", "m_second"],
    notice: (v) =>
      v.MEETING_LLM_ROUTING && v.MEETING_LLM_ROUTING !== "per_step"
        ? 'Meetings are on the older "by meeting length" routing, so the write-up and check steps are not used. Switch Model routing back under All settings.'
        : null,
  },
  {
    key: "notes",
    title: "Notes",
    unit: "per meeting-hour",
    tabUnit: "per hour",
    in: ["mic", "Recording"],
    out: ["doc", "Note"],
    steps: ["n_listen", "n_write", "n_insights"],
    extraFields: switchFields([NOTE_REPORT_FOCUSED], "My notes × meeting report"),
    card: {
      title: "My notes × meeting report",
      intro: "The report that matches a note's points to a meeting. Uses the Small helpers model. Off until you turn it on.",
      items: [NOTE_REPORT_FOCUSED],
      missing: "Arrives with the B18 backend deploy.",
    },
  },
  { key: "ask", title: "Ask AI", unit: "per 100 questions", tabUnit: "per 100 questions", estimate: "list-price estimate", noLang: true, in: ["question", "Question"], out: ["doc", "Answer"], steps: ["a_plan", "a_search", "a_answer"] },
];

// --------------------------------------------------------------- steps

export type Choice = { value: string; badge: string; name: string; note?: string; cost?: number; disabled?: string };
/** One saved field a step writes, for the change bar ("old → new"). */
export type Field = { key: string; label: string; fmt: (value: string | undefined) => string };

export type StepView = {
  id: string;
  name: string;
  why: string;
  icon: IconKey;
  verb: string;
  /** Who runs it (bold on the card) and its letter badge / logo id. */
  vendor: string;
  badge: string;
  tag?: string;
  /** Cents per hour as counted in the module total; undefined = no estimate. */
  cost?: number;
  backups: string[] | null;
  backText?: string;
  choices: Choice[];
  picked: string;
  perLang?: boolean;
  /** "Same chain as Meetings" etc. */
  shared?: string;
  keyOk: boolean | null;
  keyText: string;
  fields: Field[];
  /** On/off switches drawn in the step inspector (Listen: B16), and which are on. */
  switches?: Switch[];
  switchOn?: Record<string, boolean>;
};

const short = (label: string) => label.split(" (")[0];
const paren = (label: string) => /\(([^)]*)\)/.exec(label)?.[1];
const llmBadge = (provider: string) => ({ openai: "openai", anthropic: "anthropic", google: "google" })[provider] ?? provider;
export const BADGE_LETTER: Record<string, string> = {
  openai: "O", anthropic: "A", google: "G", azure: "A", deepgram: "D", soniox: "S", assemblyai: "A", verblizr: "V", auto: "·",
};

function keyLine(name: string, ok: boolean | undefined | null): { keyOk: boolean | null; keyText: string } {
  if (ok === undefined || ok === null) return { keyOk: null, keyText: `${name}: the server does not report this key` };
  return ok
    ? { keyOk: true, keyText: `${name} key is set on the server` }
    : { keyOk: false, keyText: `No ${name} key on the server: this step skips it` };
}

function llmModels(ctx: Ctx): Option[] {
  return ctx.catalog.LLM[ctx.provider]?.models ?? [];
}

function llmLabel(ctx: Ctx, model: string | undefined) {
  if (isRetired(model)) return RETIRED_NOTE;
  const o = llmModels(ctx).find((m) => m.value === model);
  return o ? short(o.label) : model || "Not set";
}

/** A step that is one LLM model picker. */
function llmStep(
  ctx: Ctx,
  v: Values,
  base: { id: string; name: string; why: string; icon: IconKey; verb: string; task: string; table?: Record<string, number>; weight?: number; tag?: string; fieldLabel: string },
): StepView {
  const key = `${ctx.provider.toUpperCase()}_MODEL_${base.task}`;
  const model = v[key];
  const entry = ctx.catalog.LLM[ctx.provider];
  const cost = base.table && model !== undefined ? base.table[model] : undefined;
  return {
    id: base.id,
    name: base.name,
    why: base.why,
    icon: base.icon,
    verb: base.verb,
    vendor: llmLabel(ctx, model),
    badge: llmBadge(ctx.provider),
    tag: base.tag,
    cost: cost === undefined ? undefined : cost * (base.weight ?? 1),
    backups: null,
    backText: "No backup model to set",
    choices: [
      ...retiredChoice(model ?? "", model, llmBadge(ctx.provider)),
      ...llmModels(ctx).map((m) => ({
        value: m.value,
        badge: llmBadge(ctx.provider),
        name: short(m.label),
        note: paren(m.label),
        cost: base.table?.[m.value] === undefined ? undefined : base.table[m.value] * (base.weight ?? 1),
      })),
    ],
    picked: model ?? "",
    ...keyLine(short(entry?.label ?? ctx.provider), entry?.configured),
    fields: [{ key, label: base.fieldLabel, fmt: (x) => llmLabel(ctx, x) }],
  };
}

/** Meeting / note speech to text: the saved chain for the language shown. */
function chainStep(ctx: Ctx, v: Values, id: string, why: string, shared?: string): StepView {
  const { on, order } = chainFor(v, ctx.lang);
  const first = order[0];
  const fmtOrder = (x: string | undefined) =>
    (x ?? "").split(",").filter(Boolean).map((s) => asrName(s.trim())).join(" › ") || "Not set";
  // The loanword pass is billed only when Soniox writes an Urdu transcript.
  const loanwords = ctx.lang === "urdu" && first === "soniox" && v[URDU_LOANWORDS.key] === "true" ? LOANWORDS_COST : 0;
  const cost = first ? ASR_COST[first] : undefined;
  return {
    id,
    name: "Listen",
    why,
    icon: "listen",
    verb: "listens",
    vendor: first ? asrName(first) : "Not set",
    badge: first ?? "auto",
    tag: first ? ASR_VENDORS[first]?.tag : undefined,
    cost: cost === undefined ? undefined : cost + loanwords,
    backups: on ? order.slice(1).map(asrName) : null,
    backText: on ? undefined : "Backup off: no second vendor",
    choices: ASR_VENDOR_IDS.map((x) => ({
      value: x,
      badge: x,
      name: ASR_VENDORS[x].name,
      note: ASR_VENDORS[x].note,
      cost: ASR_COST[x],
      disabled: !on && x !== "deepgram" ? "Turn the backup chain on to choose" : undefined,
    })),
    picked: first ?? "",
    perLang: true,
    shared,
    ...keyLine(first ? asrName(first) : "Speech to text", first ? ctx.catalog.ASR[first]?.configured : null),
    fields: [
      { key: "ASR_CHAIN_ENABLED", label: "Listen · Backup chain", fmt: (x) => (x === "true" ? "On" : "Off, Deepgram only") },
      ...LANGS.map((l) => ({ key: l.chainKey, label: `Listen (${l.label})`, fmt: fmtOrder })),
      ...switchFields([VOICE_RESEG, URDU_LOANWORDS], "Listen"),
    ],
    // Re-draw: any meeting Soniox writes. Loanwords: the Urdu chain only.
    switches: ctx.lang === "urdu" ? [URDU_LOANWORDS, VOICE_RESEG] : [VOICE_RESEG],
    switchOn: Object.fromEntries([URDU_LOANWORDS, VOICE_RESEG].map((s) => [s.key, v[s.key] === "true"])),
  };
}

const TTS_ORDER = ["deepgram", "openai", "azure", "google"];
const INTERP_ASR_AUTO = ["deepgram", "openai"];

function interpListen(ctx: Ctx, v: Values): StepView {
  const asr = v.ASR_PROVIDER || "auto";
  const name = (p: string) => (p === "auto" ? "Auto" : short(ctx.catalog.ASR[p]?.label ?? p));
  const backups =
    asr === "auto" ? INTERP_ASR_AUTO.slice(1) : asr === "soniox" ? INTERP_ASR_AUTO : asr === "deepgram" ? ["openai"] : [];
  const configured = Object.entries(ctx.catalog.ASR).filter(([, e]) => e?.configured).map(([p]) => p);
  const list = ["auto", ...configured.filter((p) => p !== "auto"), ...(asr !== "auto" && !configured.includes(asr) ? [asr] : [])];
  const costOf = (p: string) => LIVE_ASR_HOUR_COST[p === "auto" ? "deepgram" : p];
  return {
    id: "i_listen",
    cost: costOf(asr),
    name: "Listen",
    why: "Turns live speech into text, turn by turn.",
    icon: "listen",
    verb: "listens",
    vendor: name(asr),
    badge: asr,
    tag: asr === "soniox" ? "real-time" : asr === "auto" ? "Deepgram first" : undefined,
    backups: backups.length ? backups.map(name) : null,
    backText: backups.length ? undefined : "No backup",
    choices: list.map((p) => ({
      value: p,
      badge: p,
      name: name(p),
      cost: costOf(p),
      note: p === "auto" ? "Deepgram, then OpenAI" : p === "soniox" ? "Listens and translates in one step" : p === "deepgram" ? "Fast for English" : p === "openai" ? "Works for any language" : undefined,
      disabled: p !== "auto" && !ctx.catalog.ASR[p]?.configured ? "No key on the server" : undefined,
    })),
    picked: asr,
    ...(asr === "auto" ? { keyOk: null, keyText: "Auto uses the vendors that have a key" } : keyLine(name(asr), ctx.catalog.ASR[asr]?.configured)),
    fields: [{ key: "ASR_PROVIDER", label: "Listen", fmt: (x) => name(x || "auto") }],
  };
}

function interpTranslate(ctx: Ctx, v: Values): StepView {
  const mt = v.MT_PROVIDER || "openai";
  const model = v.OPENAI_MODEL_MT;
  const sonioxListens = (v.ASR_PROVIDER || "auto") === "soniox";
  const modelName = (m: string | undefined) => {
    if (isRetired(m)) return RETIRED_NOTE;
    const o = ctx.mtModels.find((x) => x.value === m);
    return o ? short(o.label) : m || "Not set";
  };
  const soniox = mt === "soniox" && sonioxListens;
  const sonioxKey = ctx.catalog.ASR.soniox?.configured;
  return {
    id: "i_translate",
    name: "Translate",
    why: "Puts the text into the listener's language.",
    icon: "translate",
    verb: "translates",
    vendor: soniox ? "Soniox" : modelName(model),
    cost: soniox ? 0 : model !== undefined ? MT_HOUR_COST[model] : undefined,
    badge: soniox ? "soniox" : "openai",
    tag: soniox ? "same step as Listen" : mt === "soniox" ? "Soniox needs Soniox to listen" : undefined,
    backups: soniox ? [modelName(model)] : null,
    backText: soniox ? undefined : "No backup",
    choices: [
      ...(sonioxKey || mt === "soniox"
        ? [{ value: "soniox", badge: "soniox", name: "Soniox", note: "Included when Soniox listens", cost: 0, disabled: sonioxListens ? undefined : "Only when Soniox listens" }]
        : []),
      ...retiredChoice(`model:${model ?? ""}`, model, "openai"),
      ...ctx.mtModels.map((m) => ({ value: `model:${m.value}`, badge: "openai", name: short(m.label), note: paren(m.label), cost: MT_HOUR_COST[m.value] })),
    ],
    picked: mt === "soniox" ? "soniox" : `model:${model ?? ""}`,
    ...(soniox ? keyLine("Soniox", sonioxKey) : keyLine("OpenAI", ctx.catalog.LLM.openai?.configured)),
    fields: [
      { key: "MT_PROVIDER", label: "Translate · Who translates", fmt: (x) => (x === "soniox" ? "Soniox" : "OpenAI") },
      { key: "OPENAI_MODEL_MT", label: "Translate · OpenAI model", fmt: modelName },
    ],
  };
}

function interpSpeak(ctx: Ctx, v: Values): StepView {
  const tts = v.TTS_PROVIDER || "auto";
  const name = (p: string) => (p === "auto" ? "Auto" : short(ctx.catalog.TTS[p]?.label ?? p));
  const configured = (p: string) => !!ctx.catalog.TTS[p]?.configured;
  const order = tts === "auto" ? TTS_ORDER.filter(configured) : [tts, ...TTS_ORDER.filter((p) => p !== tts && configured(p))];
  const backups = (tts === "auto" ? order : order.slice(1)).map(name);
  let tag: string | undefined;
  if (tts === "auto") tag = "fastest first";
  else if (ctx.lang === "urdu") {
    const pair = urduVoiceSteps(tts, ctx.catalog);
    tag = pair.length
      ? pair.map((s) => short(s.options.find((o) => o.value === v[s.key])?.label ?? v[s.key] ?? "")).reverse().join(" / ")
      : "no Urdu voice";
  } else {
    const m = v[`${tts.toUpperCase()}_MODEL_TTS`];
    const o = ctx.catalog.TTS[tts]?.models.find((x) => x.value === m);
    tag = o ? short(o.label) : m;
  }
  const list = ["auto", ...Object.keys(ctx.catalog.TTS).filter(configured), ...(tts !== "auto" && !configured(tts) ? [tts] : [])];
  return {
    id: "i_speak",
    name: "Speak",
    why: "Reads the translation aloud in a natural voice.",
    icon: "speak",
    verb: "speaks",
    vendor: name(tts),
    badge: tts,
    tag,
    cost: TTS_HOUR_COST[tts === "auto" ? "deepgram" : tts],
    backups: backups.length ? backups : null,
    backText: backups.length ? undefined : "No backup",
    choices: list.map((p) => ({
      value: p,
      badge: p,
      name: name(p),
      cost: TTS_HOUR_COST[p === "auto" ? "deepgram" : p],
      note:
        p === "auto" ? "Deepgram, then OpenAI, Azure, Google" : p === "azure" ? "Native voices, Urdu from Pakistan" : p === "deepgram" ? "Fastest, few languages" : p === "openai" ? "Any language, slower" : p === "google" ? "Last backup" : undefined,
      disabled: p !== "auto" && !configured(p) ? "No key on the server" : undefined,
    })),
    picked: tts,
    ...(tts === "auto" ? { keyOk: null, keyText: "Auto uses the voices that have a key" } : keyLine(name(tts), ctx.catalog.TTS[tts]?.configured)),
    fields: [
      { key: "TTS_PROVIDER", label: "Speak", fmt: (x) => name(x || "auto") },
      ...allUrduVoiceSteps(ctx.catalog).map((s) => ({
        key: s.key,
        label: `Speak · ${s.label}`,
        fmt: (x: string | undefined) => short(s.options.find((o) => o.value === x)?.label ?? x ?? "Not set"),
      })),
    ],
  };
}

/** Every step's view for the values given (draft or saved). */
export function stepView(id: string, v: Values, ctx: Ctx): StepView {
  switch (id) {
    case "i_listen":
      return interpListen(ctx, v);
    case "i_translate":
      return interpTranslate(ctx, v);
    case "i_speak":
      return interpSpeak(ctx, v);
    case "m_listen":
      return chainStep(ctx, v, id, "Turns the whole recording into text with speakers.");
    case "n_listen":
      return chainStep(ctx, v, id, "Turns the recording into text for the note.", "Same speech-to-text chain as Meetings");
    case "m_names":
      return {
        id, name: "Name speakers", why: "Matches voices to people using enrolled voiceprints.", icon: "names", verb: "names the speakers",
        vendor: "VERBLIZR voiceprints", badge: "verblizr", tag: "our own", cost: 0,
        backups: null, backText: "Speakers stay as Speaker 1, 2, 3",
        choices: [{ value: "verblizr", badge: "verblizr", name: "VERBLIZR voiceprints", note: "No outside vendor", cost: 0 }],
        picked: "verblizr", keyOk: null, keyText: "Runs on our own servers, no vendor key", fields: [],
      };
    case "m_write": {
      const P = ctx.provider.toUpperCase();
      const short_ = v[`${P}_MODEL_MEETING_REPORT`];
      const long = v[`${P}_MODEL_MEETING_REPORT_LONG`];
      const s = llmStep(ctx, v, {
        id, name: "Write minutes", why: "Writes the summary, decisions, to-dos and key points.", icon: "write", verb: "writes the minutes",
        task: "MEETING_REPORT", table: REPORT_COST, weight: 1 - LONG_SHARE, fieldLabel: "Write minutes",
      });
      const longCost = long !== undefined ? REPORT_COST[long] : undefined;
      if (long && long !== short_) s.tag = `${llmLabel(ctx, long)} from ${v.MEETING_REPORT_LONG_MIN ?? "?"} min`;
      // Same weighting as the 15-10 page: 3/4 of hours short, 1/4 long; unknown if either is.
      s.cost = s.cost !== undefined && longCost !== undefined ? s.cost + longCost * LONG_SHARE : undefined;
      s.fields.push(
        { key: `${P}_MODEL_MEETING_REPORT_LONG`, label: "Write minutes · long meetings", fmt: (x) => llmLabel(ctx, x) },
        { key: "MEETING_REPORT_LONG_MIN", label: "Write minutes · long from", fmt: (x) => `${x ?? "?"} min` },
      );
      return s;
    }
    case "m_check":
      return llmStep(ctx, v, {
        id, name: "Fact-check", why: "Checks every decision and to-do against what was said. Trust lives here.", icon: "check", verb: "fact-checks",
        task: "VERIFY_JUDGE", table: CHECK_COST, tag: "every item", fieldLabel: "Fact-check",
      });
    case "m_second":
      return llmStep(ctx, v, {
        id, name: "Second look", why: "Searches the whole transcript before an item is removed.", icon: "second", verb: "takes a second look",
        task: "RESCUE", table: LOOK_COST, tag: "before removal", fieldLabel: "Second look",
      });
    case "n_write":
      return llmStep(ctx, v, {
        id, name: "Write note", why: "Writes Meeting Notes from the transcript.", icon: "write", verb: "writes Meeting Notes",
        task: "NOTES_V2", table: NOTES_COST, tag: "Meeting Notes", fieldLabel: "Write note",
      });
    case "n_insights":
      return llmStep(ctx, v, {
        id, name: "Personal insights", why: "Turns personal voice notes into points and tasks. Also runs the medical extractors.", icon: "check", verb: "writes personal insights",
        task: "NOTES", tag: "personal notes", fieldLabel: "Personal insights",
      });
    case "a_plan":
      return llmStep(ctx, v, {
        id, name: "Plan", why: "Reads each question and picks the meetings, dates and people to search.", icon: "plan", verb: "reads the question",
        task: "ASK_PLANNER", table: ASK_PLAN_COST, tag: "planner", fieldLabel: "Plan",
      });
    case "a_search":
      return {
        id, name: "Find meetings", why: "Finds the meetings and notes that hold the answer.", icon: "search", verb: "finds the meetings",
        vendor: "OpenAI embeddings", badge: "openai", tag: "set on the server",
        backups: null, backText: "Set on the server, nothing to choose here",
        choices: [{ value: "embeddings", badge: "openai", name: "OpenAI embeddings", note: "Meaning search" }],
        picked: "embeddings", ...keyLine("OpenAI", ctx.catalog.LLM.openai?.configured), fields: [],
      };
    case "a_answer": {
      const s = llmStep(ctx, v, {
        id, name: "Answer", why: "Writes a short answer with links to the source. Uses the Small helpers model, which other steps share.", icon: "write", verb: "writes the answer",
        task: "BOARDROOM_SHORT", table: ASK_ANSWER_COST, tag: "Small helpers", fieldLabel: "Answer",
      });
      // Shared model: change it under All settings, not here.
      s.choices = s.choices.filter((c) => c.value === s.picked);
      s.backText = "Change Small helpers under All settings";
      s.fields = [];
      return s;
    }
    default:
      throw new Error(`unknown step ${id}`);
  }
}

export const moduleSteps = (m: ModuleDef, v: Values, ctx: Ctx) => m.steps.map((id) => stepView(id, v, ctx));

/** Module cost per hour (cents): the sum of the steps with an estimate, null if none has one. */
export function moduleCost(steps: StepView[]): number | null {
  const known = steps.filter((s) => s.cost !== undefined);
  return known.length ? known.reduce((a, s) => a + (s.cost ?? 0), 0) : null;
}

/** What a changed step was before, said after "Was". Call it only when a field of the step changed. */
export function wasText(now: StepView, before: StepView): string {
  if (now.vendor !== before.vendor) return before.vendor;
  if (JSON.stringify(now.backups) !== JSON.stringify(before.backups))
    return before.backups?.length ? `backup ${before.backups.join(" › ")}` : before.backText ?? "no backup";
  if (now.tag !== before.tag) return before.tag ?? before.vendor;
  const flipped = (before.switches ?? []).filter((s) => now.switchOn?.[s.key] !== before.switchOn?.[s.key]);
  if (flipped.length) return flipped.map((s) => `${s.name} ${before.switchOn?.[s.key] ? "on" : "off"}`).join(", ");
  return before.vendor;
}

// ------------------------------------------------------------- presets

export type PresetDef = { key: string; label: string; note: string; values: Values };

/**
 * Every module's presets, in one place. Recommended = what prod runs today
 * (DES 23-10-2026, backed by the B11/B12/B14/B15 evals). LLM presets are
 * written for OpenAI; with another LLM provider only Interpreter has them.
 * Chain ORDERS are never in a preset: they stay what is saved.
 */
export function presetsFor(module: ModuleKey, provider: string): PresetDef[] {
  const openai = provider === "openai";
  const MINI = "gpt-4.1-mini";
  const FULL = "gpt-4.1";
  const k = (task: string) => `OPENAI_MODEL_${task}`;
  switch (module) {
    case "interpreter":
      return [
        {
          key: "recommended",
          label: "Recommended",
          note: "Azure voice, Soniox listens and translates in one step, GPT-4o Mini if Soniox cannot.",
          values: { TTS_PROVIDER: "azure", ASR_PROVIDER: "soniox", MT_PROVIDER: "soniox", OPENAI_MODEL_MT: "gpt-4o-mini" },
        },
        {
          key: "best",
          label: "Best quality",
          note: "GPT-4o translates. Slower turns.",
          values: { TTS_PROVIDER: "azure", ASR_PROVIDER: "soniox", MT_PROVIDER: "openai", OPENAI_MODEL_MT: "gpt-4o" },
        },
      ];
    case "meetings":
      if (!openai) return [];
      return [
        {
          key: "recommended",
          label: "Recommended",
          note: "Backup chain on, GPT-4.1 Mini writes (GPT-4.1 from 90 min), GPT-4.1 checks every item.",
          values: { ASR_CHAIN_ENABLED: "true", [k("MEETING_REPORT")]: MINI, [k("MEETING_REPORT_LONG")]: FULL, MEETING_REPORT_LONG_MIN: "90", [k("VERIFY_JUDGE")]: FULL, [k("RESCUE")]: MINI },
        },
        {
          key: "cheaper",
          label: "Cheaper",
          note: "Smaller model for every step. The check lets about 7x more wrong items through.",
          values: { ASR_CHAIN_ENABLED: "true", [k("MEETING_REPORT")]: MINI, [k("MEETING_REPORT_LONG")]: MINI, MEETING_REPORT_LONG_MIN: "90", [k("VERIFY_JUDGE")]: MINI, [k("RESCUE")]: MINI },
        },
        {
          key: "best",
          label: "Best quality",
          note: "GPT-4.1 for every step, every length.",
          values: { ASR_CHAIN_ENABLED: "true", [k("MEETING_REPORT")]: FULL, [k("MEETING_REPORT_LONG")]: FULL, MEETING_REPORT_LONG_MIN: "90", [k("VERIFY_JUDGE")]: FULL, [k("RESCUE")]: FULL },
        },
      ];
    case "notes":
      if (!openai) return [];
      return [
        { key: "recommended", label: "Recommended", note: "GPT-4.1 Mini for Meeting Notes. Quality held in the 13-10 test.", values: { [k("NOTES_V2")]: MINI } },
        { key: "best", label: "Best quality", note: "GPT-4.1 for Meeting Notes, about 2.5x the cost.", values: { [k("NOTES_V2")]: FULL } },
      ];
    case "ask":
      if (!openai) return [];
      return [{ key: "recommended", label: "Recommended", note: "GPT-4.1 Mini plans. Nano lost names and question types in the 21-10 test.", values: { [k("ASK_PLANNER")]: MINI } }];
  }
}

/** The preset the values match, or null ("Custom setup"). A missing value never matches. */
export function presetOf(presets: PresetDef[], v: Values): PresetDef | null {
  for (const p of presets) {
    if (Object.entries(p.values).every(([key, want]) => v[key] !== undefined && v[key] === want)) return p;
  }
  return null;
}

/** Every saved field a module's steps write (for the unsaved chip and the Save bar). */
export function moduleFields(m: ModuleDef, v: Values, ctx: Ctx): Field[] {
  const seen = new Set<string>();
  return [...moduleSteps(m, v, ctx).flatMap((s) => s.fields), ...(m.extraFields ?? [])]
    .filter((f) => !seen.has(f.key) && seen.add(f.key));
}

export const changedKeys = (fields: Field[], draft: Values, saved: Values) =>
  fields.filter((f) => (draft[f.key] ?? "") !== (saved[f.key] ?? ""));

/** The draft patch for choosing `value` as a step's main vendor / model ({} = nothing to write). */
export function pickPatch(step: StepView, value: string, v: Values, ctx: Ctx): Values {
  if (step.choices.find((c) => c.value === value)?.disabled) return {};
  switch (step.id) {
    case "i_listen":
      return { ASR_PROVIDER: value };
    case "i_speak":
      return { TTS_PROVIDER: value };
    case "i_translate":
      return value === "soniox" ? { MT_PROVIDER: "soniox" } : { MT_PROVIDER: "openai", OPENAI_MODEL_MT: value.replace(/^model:/, "") };
    case "m_listen":
    case "n_listen": {
      const { on, order } = chainFor(v, ctx.lang);
      return on ? chainPatch(ctx.lang, withFirst(order, value)) : {};
    }
    default:
      return step.fields[0] ? { [step.fields[0].key]: value } : {};
  }
}
